import { Check, Flame } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import type { StreakWeekDay } from "@/types/streak"

/**
 * OKS-515 — §12 "THIS WEEK" 7-dot strip.
 *
 * Renders the current brand-tz week (Monday-first) as seven day-dots:
 *   - done     → filled accent circle with a check
 *   - today    → accent ring with the flame outline
 *   - upcoming → ghost circle (future, or a past un-posted day — UX §2
 *                defines no distinct "missed" treatment)
 *
 * The day states are derived server-side (`Streaks::WeekQuery`) from the
 * membership's submissions — never on the client. The single-letter label is
 * the first character of the localized full weekday name (M T W T F S S /
 * L M M J V S D), and the full name + translated state drive the per-dot
 * accessible label (UX Spec §6 — meaning never by colour alone).
 *
 * 7-column flex stays even down to 320px (UX Spec §5); dots are 34px and
 * shrink-to-fit. Static — no JS animation.
 */
interface StreakWeekStripProps {
  days: StreakWeekDay[]
}

export function StreakWeekStrip({ days }: StreakWeekStripProps) {
  const { t } = useTranslation()

  return (
    <section
      data-testid="streak-week-strip"
      aria-label={t("streak.detail.this_week.title")}
    >
      <h2 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wide">
        {t("streak.detail.this_week.title")}
      </h2>
      <ol className="flex items-start justify-between gap-1">
        {days.map((day) => {
          const fullName = t(`streak.detail.this_week.full.${day.weekday}`)
          const letter = fullName.charAt(0).toUpperCase()
          const stateLabel = t(`streak.detail.this_week.state.${day.state}`)
          return (
            <li
              key={day.date}
              className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
              data-testid="streak-week-dot"
              data-state={day.state}
            >
              <span
                aria-label={t("streak.detail.this_week.aria", {
                  day: fullName,
                  state: stateLabel,
                })}
                className={cn(
                  "flex size-[34px] shrink-0 items-center justify-center rounded-full",
                  day.state === "done" && "bg-[#ff5c00] text-white",
                  day.state === "today" &&
                    "text-[#ff5c00] ring-2 ring-[#ff5c00] ring-offset-1",
                  day.state === "upcoming" &&
                    "bg-muted text-muted-foreground/40",
                )}
              >
                {day.state === "done" && (
                  <Check aria-hidden="true" className="size-4" />
                )}
                {day.state === "today" && (
                  <Flame aria-hidden="true" className="size-4" />
                )}
              </span>
              <span
                aria-hidden="true"
                className="text-muted-foreground text-xs"
              >
                {letter}
              </span>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
